import { Controller } from "@hotwired/stimulus"

// Dev/QA-only badge that shows Turbo navigation finish time.
// Thresholds: fast < 500ms, warn < 1500ms, slow >= 1500ms.
const MEASURED_FRAMES = new Set(["admin_content", "modal", "sidepanel"])

export default class extends Controller {
  static targets = ["value"]
  static values = {
    warnMs: { type: Number, default: 500 },
    slowMs: { type: Number, default: 1500 },
  }

  connect() {
    this.startedAt = null
    this.pendingFrameId = null

    this.boundStartVisit = this.startVisit.bind(this)
    this.boundStartSubmit = this.startSubmit.bind(this)
    this.boundStartFrameFetch = this.startFrameFetch.bind(this)
    this.boundFinishVisit = this.finishVisit.bind(this)
    this.boundFinishFrame = this.finishFrame.bind(this)

    document.addEventListener("turbo:before-visit", this.boundStartVisit)
    document.addEventListener("turbo:submit-start", this.boundStartSubmit)
    document.addEventListener("turbo:before-fetch-request", this.boundStartFrameFetch)
    document.addEventListener("turbo:load", this.boundFinishVisit)
    document.addEventListener("turbo:frame-load", this.boundFinishFrame)

    this.reportInitialLoad()
  }

  disconnect() {
    document.removeEventListener("turbo:before-visit", this.boundStartVisit)
    document.removeEventListener("turbo:submit-start", this.boundStartSubmit)
    document.removeEventListener("turbo:before-fetch-request", this.boundStartFrameFetch)
    document.removeEventListener("turbo:load", this.boundFinishVisit)
    document.removeEventListener("turbo:frame-load", this.boundFinishFrame)
  }

  startVisit() {
    this.startedAt = performance.now()
    this.pendingFrameId = null
    this.setPending()
  }

  startSubmit() {
    this.startedAt = performance.now()
    this.pendingFrameId = null
    this.setPending()
  }

  startFrameFetch(event) {
    const frameId = this.frameIdFromFetchEvent(event)
    if (!frameId || !MEASURED_FRAMES.has(frameId)) return

    this.startedAt = performance.now()
    this.pendingFrameId = frameId
    this.setPending()
  }

  frameIdFromFetchEvent(event) {
    const headers = event.detail?.fetchOptions?.headers
    const headerFrameId = headers?.["Turbo-Frame"] || headers?.get?.("Turbo-Frame")
    if (headerFrameId && headerFrameId !== "_top") return headerFrameId

    const target = event.target
    if (target instanceof Element && target.tagName === "TURBO-FRAME") {
      return target.id
    }

    return null
  }

  finishVisit() {
    if (this.startedAt == null) return
    if (this.pendingFrameId) return

    this.render(performance.now() - this.startedAt)
    this.startedAt = null
  }

  finishFrame(event) {
    const frame = event.target
    if (!(frame instanceof Element)) return
    if (frame.id !== this.pendingFrameId || this.startedAt == null) return

    this.render(performance.now() - this.startedAt)
    this.startedAt = null
    this.pendingFrameId = null
  }

  reportInitialLoad() {
    const entries = performance.getEntriesByType("navigation")
    const nav = entries[entries.length - 1]
    if (nav && Number.isFinite(nav.duration) && nav.duration > 0) {
      this.render(nav.duration)
      return
    }

    // Fallback when Navigation Timing is unavailable (e.g. some test envs).
    if (performance.timing?.navigationStart) {
      const duration = performance.now()
      if (duration > 0) this.render(duration)
    }
  }

  setPending() {
    this.element.dataset.state = "pending"
    this.element.classList.remove(
      "page-load-timer--fast",
      "page-load-timer--warn",
      "page-load-timer--slow"
    )
    if (this.hasValueTarget) {
      this.valueTarget.textContent = "…"
    }
  }

  render(durationMs) {
    const ms = Math.max(0, Math.round(durationMs))
    const state = this.stateFor(ms)

    this.element.dataset.state = state
    this.element.classList.remove(
      "page-load-timer--fast",
      "page-load-timer--warn",
      "page-load-timer--slow"
    )
    this.element.classList.add(`page-load-timer--${state}`)

    if (this.hasValueTarget) {
      this.valueTarget.textContent = this.format(ms)
    }

    if (state === "slow") {
      this.element.classList.remove("page-load-timer--pulse")
      requestAnimationFrame(() => {
        this.element.classList.add("page-load-timer--pulse")
      })
    } else {
      this.element.classList.remove("page-load-timer--pulse")
    }
  }

  stateFor(ms) {
    if (ms >= this.slowMsValue) return "slow"
    if (ms >= this.warnMsValue) return "warn"
    return "fast"
  }

  format(ms) {
    if (ms >= 1000) {
      return `${(ms / 1000).toFixed(2)}s`
    }
    return `${ms}ms`
  }
}
